import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Card, 
    CardContent, 
    Typography, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow,
    Paper,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Grid,
    CircularProgress,
    TextField,
    Button,
    Switch,
    FormControlLabel
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';

const CountryStats = () => {
    const [loading, setLoading] = useState(true);
    const [countryStats, setCountryStats] = useState([]);
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [period, setPeriod] = useState('today');
    const [sortBy, setSortBy] = useState('clicks');
    const [sortDirection, setSortDirection] = useState('desc');
    const [updateMessage, setUpdateMessage] = useState('');
    const [showLocalIPs, setShowLocalIPs] = useState(false);

    useEffect(() => {
        // Установка дат в зависимости от выбранного периода
        const now = new Date();
        
        switch(period) {
            case 'today':
                setStartDate(new Date(now.setHours(0, 0, 0, 0)));
                setEndDate(new Date());
                break;
            case 'yesterday':
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                yesterday.setHours(0, 0, 0, 0);
                setStartDate(yesterday);
                const yesterdayEnd = new Date(yesterday);
                yesterdayEnd.setHours(23, 59, 59, 999);
                setEndDate(yesterdayEnd);
                break;
            case 'week':
                const weekStart = new Date(now);
                weekStart.setDate(weekStart.getDate() - 6);
                weekStart.setHours(0, 0, 0, 0);
                setStartDate(weekStart);
                setEndDate(new Date());
                break;
            case 'month':
                const monthStart = new Date(now);
                monthStart.setMonth(monthStart.getMonth() - 1);
                monthStart.setHours(0, 0, 0, 0);
                setStartDate(monthStart);
                setEndDate(new Date());
                break;
            default:
                // Не меняем даты, если выбран custom период
                break;
        }
    }, [period]);

    useEffect(() => {
        if (startDate && endDate) {
            fetchCountryStats();
        }
    }, [startDate, endDate]);

    const fetchCountryStats = async (forceRefresh = false) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            
            // Клонируем даты, чтобы не менять исходные
            const startDateTime = new Date(startDate);
            startDateTime.setHours(0, 0, 0, 0);
            
            const endDateTime = new Date(endDate);
            endDateTime.setHours(23, 59, 59, 999);
            
            // Базовые параметры запроса
            const params = {
                startDate: startDateTime.toISOString().split('T')[0],
                endDate: endDateTime.toISOString().split('T')[0]
            };
            
            // Если нужно принудительное обновление
            if (forceRefresh) {
                params._t = new Date().getTime();
                params.nocache = Math.random();
            }
            
            const response = await axios.get('/api/admin/stats/countries', {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Cache-Control': forceRefresh ? 'no-cache, no-store, must-revalidate' : undefined,
                    'Pragma': forceRefresh ? 'no-cache' : undefined,
                    'Expires': forceRefresh ? '0' : undefined
                },
                params
            });
            
            if (response.data && Array.isArray(response.data)) {
                setCountryStats(response.data);
            }
        } catch (error) {
            console.error('Ошибка при получении статистики по странам:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        // Очищаем предыдущее сообщение об обновлении
        setUpdateMessage('');
        
        // Показываем индикатор загрузки
        setLoading(true);
        
        // Принудительно обновляем данные
        fetchCountryStats(true)
            .then(() => {
                // Показываем сообщение об успешном обновлении
                const currentTime = new Date().toLocaleTimeString();
                setUpdateMessage(`Данные успешно обновлены в ${currentTime}`);
                
                // Скрываем сообщение через 5 секунд
                setTimeout(() => {
                    setUpdateMessage('');
                }, 5000);
            })
            .catch(error => {
                console.error('Ошибка при обновлении данных:', error);
                setUpdateMessage('Ошибка при обновлении данных. Проверьте соединение с сервером.');
            });
    };

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortDirection('desc');
        }
    };

    const handlePeriodChange = (event) => {
        setPeriod(event.target.value);
    };

    // Форматирование даты в строку для ввода
    const formatDateForInput = (date) => {
        if (!date) return '';
        return date.toISOString().split('T')[0];
    };

    const handleStartDateChange = (event) => {
        setStartDate(new Date(event.target.value));
        setPeriod('custom');
    };

    const handleEndDateChange = (event) => {
        setEndDate(new Date(event.target.value));
        setPeriod('custom');
    };

    // Фильтруем страны, исключая локальные IP, если не включен showLocalIPs
    const filteredCountryStats = countryStats.filter(country => 
        showLocalIPs || country.countryCode !== 'LOCAL'
    );
    
    const sortedCountryStats = [...filteredCountryStats].sort((a, b) => {
        const valueA = a[sortBy];
        const valueB = b[sortBy];
        
        if (sortDirection === 'asc') {
            return valueA - valueB;
        } else {
            return valueB - valueA;
        }
    });

    // Рассчитываем общую статистику только по отфильтрованным странам
    const totalClicks = filteredCountryStats.reduce((sum, country) => sum + country.clicks, 0);
    const totalLeads = filteredCountryStats.reduce((sum, country) => sum + (country.leads || 0), 0);

    // Функция для отображения флага страны
    const getCountryEmoji = (countryCode) => {
        if (!countryCode || countryCode === 'XX') return '';
        
        // Конвертируем ISO код страны в emoji флаг
        const codePoints = countryCode
            .toUpperCase()
            .split('')
            .map(char => 127397 + char.charCodeAt());
        
        return String.fromCodePoint(...codePoints);
    };

    return (
        <Box>
            <Typography variant="h5" component="h2" gutterBottom>
                Статистика по странам
            </Typography>
            
            {/* Сообщение об обновлении */}
            {updateMessage && (
                <Paper 
                    sx={{ 
                        p: 1, 
                        mb: 2, 
                        bgcolor: updateMessage.includes('Ошибка') ? 'error.light' : 'success.light',
                        color: 'white',
                        display: 'flex',
                        justifyContent: 'center'
                    }}
                >
                    <Typography>{updateMessage}</Typography>
                </Paper>
            )}

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                        <InputLabel id="period-select-label">Период</InputLabel>
                        <Select
                            labelId="period-select-label"
                            id="period-select"
                            value={period}
                            label="Период"
                            onChange={handlePeriodChange}
                        >
                            <MenuItem value="today">Сегодня</MenuItem>
                            <MenuItem value="yesterday">Вчера</MenuItem>
                            <MenuItem value="week">Последние 7 дней</MenuItem>
                            <MenuItem value="month">Последние 30 дней</MenuItem>
                            <MenuItem value="custom">Произвольный период</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                
                {period === 'custom' && (
                    <>
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="С даты"
                                type="date"
                                value={formatDateForInput(startDate)}
                                onChange={handleStartDateChange}
                                fullWidth
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="По дату"
                                type="date"
                                value={formatDateForInput(endDate)}
                                onChange={handleEndDateChange}
                                fullWidth
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                inputProps={{
                                    min: formatDateForInput(startDate)
                                }}
                            />
                        </Grid>
                    </>
                )}
                
                <Grid item xs={12} md={period === 'custom' ? 12 : 8}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showLocalIPs}
                                    onChange={(e) => setShowLocalIPs(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Показывать локальные IP"
                            sx={{ mr: 2 }}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleRefresh}
                            sx={{ minWidth: 'auto', height: 56 }}
                            startIcon={<RefreshIcon />}
                        >
                            Обновить
                        </Button>
                    </Box>
                </Grid>
            </Grid>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Сортировать по</InputLabel>
                <Select
                    value={sortBy}
                    label="Сортировать по"
                    onChange={(e) => setSortBy(e.target.value)}
                >
                    <MenuItem value="clicks">Количеству кликов</MenuItem>
                    <MenuItem value="leads">Количеству лидов</MenuItem>
                    <MenuItem value="conversionRate">Конверсии</MenuItem>
                    <MenuItem value="approveRate">Approve Rate</MenuItem>
                    <MenuItem value="revenue">Доходу</MenuItem>
                    <MenuItem value="cost">Расходу</MenuItem>
                    <MenuItem value="profit">Прибыли</MenuItem>
                    <MenuItem value="roi">ROI</MenuItem>
                </Select>
            </FormControl>

            <Card>
                <CardContent>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body1">
                                    Всего кликов: <strong>{totalClicks}</strong>
                                </Typography>
                                <Typography variant="body1">
                                    Всего лидов: <strong>{totalLeads}</strong>
                                </Typography>
                                <Typography variant="body1">
                                    Общая конверсия: <strong>{totalClicks > 0 ? ((totalLeads / totalClicks) * 100).toFixed(2) : 0}%</strong>
                                </Typography>
                                <Typography variant="body1">
                                    Общий расход: <strong>{filteredCountryStats.reduce((sum, c) => sum + (c.cost || 0), 0).toFixed(2)} руб.</strong>
                                </Typography>
                                <Typography variant="body1">
                                    Общий доход: <strong>{filteredCountryStats.reduce((sum, c) => sum + (c.confirmedRevenue || 0), 0).toFixed(2)} руб.</strong>
                                </Typography>
                                <Typography variant="body1">
                                    Общая прибыль: <strong>{filteredCountryStats.reduce((sum, c) => sum + ((c.confirmedRevenue || 0) - (c.cost || 0)), 0).toFixed(2)} руб.</strong>
                                </Typography>
                            </Box>

                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Страна</TableCell>
                                            <TableCell 
                                                onClick={() => handleSort('clicks')}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                Количество кликов {sortBy === 'clicks' && (sortDirection === 'asc' ? '↑' : '↓')}
                                            </TableCell>
                                            <TableCell 
                                                onClick={() => handleSort('leads')}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                Количество лидов {sortBy === 'leads' && (sortDirection === 'asc' ? '↑' : '↓')}
                                            </TableCell>
                                            <TableCell 
                                                onClick={() => handleSort('conversionRate')}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                CR % {sortBy === 'conversionRate' && (sortDirection === 'asc' ? '↑' : '↓')}
                                            </TableCell>
                                            <TableCell 
                                                onClick={() => handleSort('approveRate')}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                Approve Rate % {sortBy === 'approveRate' && (sortDirection === 'asc' ? '↑' : '↓')}
                                            </TableCell>
                                            <TableCell 
                                                onClick={() => handleSort('cost')}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                Расход {sortBy === 'cost' && (sortDirection === 'asc' ? '↑' : '↓')}
                                            </TableCell>
                                            <TableCell 
                                                onClick={() => handleSort('revenue')}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                Доход {sortBy === 'revenue' && (sortDirection === 'asc' ? '↑' : '↓')}
                                            </TableCell>
                                            <TableCell 
                                                onClick={() => handleSort('profit')}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                Прибыль {sortBy === 'profit' && (sortDirection === 'asc' ? '↑' : '↓')}
                                            </TableCell>
                                            <TableCell 
                                                onClick={() => handleSort('roi')}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                ROI % {sortBy === 'roi' && (sortDirection === 'asc' ? '↑' : '↓')}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sortedCountryStats.map((country) => (
                                            <TableRow key={country.countryCode}>
                                                <TableCell>
                                                    <span style={{ marginRight: '8px', fontSize: '1.2em' }}>
                                                        {getCountryEmoji(country.countryCode)}
                                                    </span>
                                                    {country.countryName}
                                                </TableCell>
                                                <TableCell>{country.clicks}</TableCell>
                                                <TableCell>{country.leads || 0}</TableCell>
                                                <TableCell>
                                                    {country.clicks > 0 
                                                        ? ((country.leads || 0) / country.clicks * 100).toFixed(2) 
                                                        : 0}%
                                                </TableCell>
                                                <TableCell>
                                                    {(country.approveRate || 0).toFixed(2)}%
                                                </TableCell>
                                                <TableCell>
                                                    {(country.cost || 0).toFixed(2)} руб.
                                                </TableCell>
                                                <TableCell>
                                                    {(country.confirmedRevenue || 0).toFixed(2)} руб.
                                                </TableCell>
                                                <TableCell>
                                                    {((country.confirmedRevenue || 0) - (country.cost || 0)).toFixed(2)} руб.
                                                </TableCell>
                                                <TableCell>
                                                    {(country.roi || 0).toFixed(2)}%
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {filteredCountryStats.length > 0 && (
                                            <TableRow
                                                sx={{ 
                                                    '& td': { 
                                                        fontWeight: 'bold', 
                                                        borderTop: '2px solid rgba(224, 224, 224, 1)'
                                                    } 
                                                }}
                                            >
                                                <TableCell component="th" scope="row">ИТОГО</TableCell>
                                                <TableCell>{totalClicks}</TableCell>
                                                <TableCell>{totalLeads}</TableCell>
                                                <TableCell>
                                                    {totalClicks > 0 ? ((totalLeads / totalClicks) * 100).toFixed(2) : 0}%
                                                </TableCell>
                                                <TableCell>
                                                    {totalLeads > 0 
                                                        ? (filteredCountryStats.reduce((sum, c) => sum + (c.confirmedLeads || 0), 0) / totalLeads * 100).toFixed(2) 
                                                        : 0}%
                                                </TableCell>
                                                <TableCell>
                                                    {filteredCountryStats.reduce((sum, c) => sum + (c.cost || 0), 0).toFixed(2)} руб.
                                                </TableCell>
                                                <TableCell>
                                                    {filteredCountryStats.reduce((sum, c) => sum + (c.confirmedRevenue || 0), 0).toFixed(2)} руб.
                                                </TableCell>
                                                <TableCell>
                                                    {filteredCountryStats.reduce((sum, c) => sum + ((c.confirmedRevenue || 0) - (c.cost || 0)), 0).toFixed(2)} руб.
                                                </TableCell>
                                                <TableCell>
                                                    {(() => {
                                                        const totalCost = filteredCountryStats.reduce((sum, c) => sum + (c.cost || 0), 0);
                                                        const totalProfit = filteredCountryStats.reduce((sum, c) => sum + ((c.confirmedRevenue || 0) - (c.cost || 0)), 0);
                                                        return totalCost > 0 ? (totalProfit / totalCost * 100).toFixed(2) : 0;
                                                    })()}%
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default CountryStats;